
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Moment } from 'moment-timezone';
import linkifyStr from 'linkify-string';
import OrderedQueue from '@/types/OrderedQueue';
import { QueueEntry } from '@/types/QueueEntry';
import ErrorDialog from '@/util/ErrorDialog';
import EscapeHTML from '@/util/Sanitization';
import * as PromptHandler from '@/util/PromptHandler';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faUser,
	faAt,
	faQuestion,
	faLink,
	faClock,
	faCheck,
	faSortNumericUp,
	faSortNumericDown,
	faTimes,
	faThumbtack,
	faEnvelope,
	faHandsHelping,
	faFrownOpen,
	faMapMarker,
	faCircle,
	faChalkboardTeacher,
	faUndo,
} from '@fortawesome/free-solid-svg-icons';

library.add(
	faUser,
	faAt,
	faQuestion,
	faLink,
	faClock,
	faCheck,
	faSortNumericUp,
	faSortNumericDown,
	faTimes,
	faThumbtack,
	faEnvelope,
	faHandsHelping,
	faFrownOpen,
	faMapMarker,
	faCircle,
	faChalkboardTeacher,
	faUndo
);

@Component
export default class QueueEntryDisplay extends Vue {
	@Prop({ required: true }) entry!: QueueEntry;
	@Prop({ required: true }) stack!: boolean;
	@Prop({ required: true }) queue!: OrderedQueue;
	@Prop({ required: true }) admin!: boolean;
	@Prop({ required: true }) time!: Moment;

	get anonymous() {
		return !(
			this.admin ||
			(this.$root.$data.userInfo.email !== undefined &&
				this.entry.email === this.$root.$data.userInfo.email)
		);
	}

	get name() {
		return this.anonymous ? 'Anonymous Student' : this.entry.name;
	}

	get location() {
		return linkifyStr(this.entry.location || '', {
			defaultProtocol: 'https',
		});
	}

	get humanizedTimestamp() {
		// HACK: fix time update lag issues in the beginning
		// by saying the time is 5 seconds ahead of what it really is.
		// Since we only display "a few seconds ago" this shouldn't have
		// any noticeable impact.
		return this.entry.humanizedTimestamp(this.time.clone().add(5, 'second'));
	}

	get promptResponses(): string[] {
		return PromptHandler.descriptionToResponses(this.entry.description);
	}

	get hasCustomPrompts(): boolean {
		return this.promptResponses.length > 0;
	}

	removeRequestRunning = false;
	removeEntry() {
		this.queue.personallyRemovedEntries.add(this.entry.id);
		this.removeRequestRunning = true;
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/entries/${this.entry.id}`,
			{
				method: 'DELETE',
			}
		).then((res) => {
			this.removeRequestRunning = false;
			if (res.status !== 204) {
				return ErrorDialog(res);
			}
		});
	}

	pinEntryRequestRunning = false;
	pinEntry() {
		this.pinEntryRequestRunning = true;
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/entries/${this.entry.id}/pin`,
			{
				method: 'POST',
			}
		).then((res) => {
			this.pinEntryRequestRunning = false;
			if (res.status !== 204) {
				return ErrorDialog(res);
			}

			this.$buefy.toast.open({
				duration: 5000,
				message: `Pinned ${EscapeHTML(this.entry.email!)}!`,
				type: 'is-success',
			});
		});
	}

	helpingRequestRunning = false;
	setHelping(helping: boolean) {
		this.helpingRequestRunning = true;
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/entries/${this.entry.id}/helping?helping=${helping}`,
			{
				method: 'PUT',
			}
		).then((res) => {
			this.helpingRequestRunning = false;
			if (res.status !== 204) {
				return ErrorDialog(res);
			}
		});
	}

	get beingHelpedBy() {
		return EscapeHTML(this.entry.helping.trim());
	}

	notHelpedRequestRunning = false;
	setNotHelped() {
		this.notHelpedRequestRunning = true;
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/entries/${this.entry.id}/helped`,
			{
				method: 'DELETE',
			}
		).then((res) => {
			this.notHelpedRequestRunning = false;
			if (res.status !== 204) {
				return ErrorDialog(res);
			}
		});
	}

	messageUser() {
		this.$buefy.dialog.prompt({
			message: `Send message to ${EscapeHTML(this.entry.email!)}:`,
			inputAttrs: {
				placeholder: 'Your meeting is empty, please come back!',
			},
			trapFocus: true,
			onConfirm: (message) => {
				fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/messages`, {
					method: 'POST',
					body: JSON.stringify({
						receiver: this.entry.email,
						content: message,
					}),
				}).then((res) => {
					if (res.status !== 201) {
						return ErrorDialog(res);
					}

					this.$buefy.toast.open({
						duration: 5000,
						message: `Sent message to ${EscapeHTML(this.entry.email!)}`,
						type: 'is-success',
					});
				});
			},
		});
	}
}
